import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Save, CheckCircle, XCircle } from "lucide-react";

const BUDGET_BANDS = [
  "£0–£50",
  "£50–£150",
  "£150–£250",
  "£250–£500",
  "£500–£1,000",
  "£1,000+",
];

interface Setting { key: string; value: number; label?: string | null; }
interface LeadPrice { id: number; category: string; budgetBand: string; price: number; }
interface StripeKeys {
  publishableKey: string;
  secretKeySet: boolean;
  secretKeyMasked: string;
  connectClientId: string;
  webhookSecretSet: boolean;
  webhookSecretMasked: string;
  orderWebhookSecretSet: boolean;
  orderWebhookSecretMasked: string;
  itemDeliveryWebhookSecretSet: boolean;
  itemDeliveryWebhookSecretMasked: string;
}

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function PriceInput({ value, onSave }: { value: number; onSave: (v: number) => Promise<void> }) {
  const [local, setLocal] = useState((value / 100).toFixed(2));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocal((value / 100).toFixed(2)); }, [value]);

  const handleSave = async () => {
    const num = parseFloat(local);
    if (isNaN(num) || num < 0) return;
    setSaving(true);
    try { await onSave(Math.round(num * 100)); } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-1">
      <span className="text-slate-400 text-sm">£</span>
      <input
        type="number"
        min="0"
        step="0.5"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={handleSave}
        onKeyDown={e => { if (e.key === "Enter") handleSave(); }}
        className="w-20 border border-slate-200 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary/20"
        disabled={saving}
      />
    </div>
  );
}

function StripeSection() {
  const { toast } = useToast();
  const [keys, setKeys] = useState<StripeKeys | null>(null);
  const [pubKey, setPubKey] = useState("");
  const [secKey, setSecKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [orderWebhookSecret, setOrderWebhookSecret] = useState("");
  const [itemDeliveryWebhookSecret, setItemDeliveryWebhookSecret] = useState("");
  const [showSec, setShowSec] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);
  const [showOrderWebhook, setShowOrderWebhook] = useState(false);
  const [showItemDeliveryWebhook, setShowItemDeliveryWebhook] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadKeys = () =>
    fetch("/api/admin/stripe/keys", { headers: authHeaders() })
      .then(r => r.json())
      .then((d: StripeKeys) => {
        setKeys(d);
        setPubKey(d.publishableKey ?? "");
      })
      .catch(() => {});

  useEffect(() => { void loadKeys(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: Record<string, string> = { publishableKey: pubKey };
      if (secKey.trim()) body["secretKey"] = secKey.trim();
      if (webhookSecret.trim()) body["webhookSecret"] = webhookSecret.trim();
      if (orderWebhookSecret.trim()) body["orderWebhookSecret"] = orderWebhookSecret.trim();
      if (itemDeliveryWebhookSecret.trim()) body["itemDeliveryWebhookSecret"] = itemDeliveryWebhookSecret.trim();
      await fetch("/api/admin/stripe/keys", { method: "PUT", headers: authHeaders(), body: JSON.stringify(body) });
      toast({ title: "Stripe settings saved!" });
      await loadKeys();
      setSecKey("");
      setWebhookSecret("");
      setOrderWebhookSecret("");
      setItemDeliveryWebhookSecret("");
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Stripe Integration</h2>
        <p className="text-sm text-slate-500 mt-1">
          Connect your Stripe account so payments can be collected when users post events, deals, or reveal job leads.
          Get your API keys from{" "}
          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-primary underline">dashboard.stripe.com/apikeys</a>.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-lg">
        {/* Status badges */}
        {keys && (
          <div className="flex flex-wrap gap-3 text-sm">
            <div className="flex items-center gap-1.5">
              {keys.secretKeySet
                ? <><CheckCircle size={14} className="text-emerald-500" /><span className="text-emerald-700 font-medium">Stripe connected</span></>
                : <><XCircle size={14} className="text-amber-500" /><span className="text-amber-700 font-medium">Keys not set</span></>}
            </div>
            <div className="flex items-center gap-1.5">
              {keys.webhookSecretSet
                ? <><CheckCircle size={14} className="text-emerald-500" /><span className="text-emerald-700 font-medium">Subscription webhook set</span></>
                : <><XCircle size={14} className="text-slate-400" /><span className="text-slate-500 font-medium">Subscription webhook not set</span></>}
            </div>
            <div className="flex items-center gap-1.5">
              {keys.orderWebhookSecretSet
                ? <><CheckCircle size={14} className="text-emerald-500" /><span className="text-emerald-700 font-medium">Order webhook set</span></>
                : <><XCircle size={14} className="text-amber-500" /><span className="text-amber-700 font-medium">Order webhook not set</span></>}
            </div>
            <div className="flex items-center gap-1.5">
              {keys.itemDeliveryWebhookSecretSet
                ? <><CheckCircle size={14} className="text-emerald-500" /><span className="text-emerald-700 font-medium">Hub Dash webhook set</span></>
                : <><XCircle size={14} className="text-amber-500" /><span className="text-amber-700 font-medium">Hub Dash webhook not set</span></>}
            </div>
          </div>
        )}

        {/* API Keys */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">API Keys</p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">Publishable Key (pk_…)</label>
              <input
                value={pubKey}
                onChange={e => setPubKey(e.target.value)}
                placeholder="pk_live_..."
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-primary transition-colors font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">
                Secret Key (sk_…)
                {keys?.secretKeySet && <span className="ml-2 normal-case font-normal text-slate-400">— currently set ({keys.secretKeyMasked})</span>}
              </label>
              <div className="relative">
                <input
                  value={secKey}
                  onChange={e => setSecKey(e.target.value)}
                  placeholder={keys?.secretKeySet ? "Enter new key to replace" : "sk_live_..."}
                  type={showSec ? "text" : "password"}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm outline-none focus:border-primary transition-colors font-mono"
                />
                <button onClick={() => setShowSec(!showSec)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showSec ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Stored securely and never shown in full.</p>
            </div>
          </div>
        </div>

        {/* Stripe Connect */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Stripe Connect</p>
          <p className="text-xs text-slate-400">
            Shop owners link their Stripe account via Express onboarding — no Client ID needed.
            Payments go directly to the shop's Stripe account, with your platform fee routed automatically.
          </p>
        </div>

        {/* Subscription Webhook */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Subscription Webhook</p>
          <p className="text-xs text-slate-400 mb-3">
            Add a webhook in your{" "}
            <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="text-primary underline">Stripe Dashboard</a>
            {" "}pointing to <code className="bg-slate-100 px-1 rounded text-xs">/api/order/stripe-subscription-webhook</code> for events{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">customer.subscription.updated</code> and{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">customer.subscription.deleted</code>.
            Paste the signing secret below.
          </p>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">
              Signing Secret (whsec_…)
              {keys?.webhookSecretSet && <span className="ml-2 normal-case font-normal text-slate-400">— currently set ({keys.webhookSecretMasked})</span>}
            </label>
            <div className="relative">
              <input
                value={webhookSecret}
                onChange={e => setWebhookSecret(e.target.value)}
                placeholder={keys?.webhookSecretSet ? "Enter new secret to replace" : "whsec_..."}
                type={showWebhook ? "text" : "password"}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm outline-none focus:border-primary transition-colors font-mono"
              />
              <button onClick={() => setShowWebhook(!showWebhook)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showWebhook ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* Order Webhook */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Order Webhook</p>
          <p className="text-xs text-slate-400 mb-3">
            Add a webhook in your{" "}
            <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="text-primary underline">Stripe Dashboard</a>
            {" "}pointing to <code className="bg-slate-100 px-1 rounded text-xs">https://www.ayrshirehub.co.uk/api/order/stripe-order-webhook</code> for events{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">payment_intent.succeeded</code> and{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">transfer.failed</code>.
            This detects orphaned payments and failed store payouts. Paste the signing secret below.
          </p>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">
              Signing Secret (whsec_…)
              {keys?.orderWebhookSecretSet && <span className="ml-2 normal-case font-normal text-slate-400">— currently set ({keys.orderWebhookSecretMasked})</span>}
            </label>
            <div className="relative">
              <input
                value={orderWebhookSecret}
                onChange={e => setOrderWebhookSecret(e.target.value)}
                placeholder={keys?.orderWebhookSecretSet ? "Enter new secret to replace" : "whsec_..."}
                type={showOrderWebhook ? "text" : "password"}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm outline-none focus:border-primary transition-colors font-mono"
              />
              <button onClick={() => setShowOrderWebhook(!showOrderWebhook)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showOrderWebhook ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* Hub Dash Webhook */}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Hub Dash Webhook</p>
          <p className="text-xs text-slate-400 mb-3">
            Add a webhook in your{" "}
            <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="text-primary underline">Stripe Dashboard</a>
            {" "}pointing to <code className="bg-slate-100 px-1 rounded text-xs">https://www.ayrshirehub.co.uk/api/item-delivery/stripe-webhook</code> for event{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">checkout.session.completed</code>.
            This is the recovery fallback — if a customer's app closes after payment, this creates their delivery request.
            Paste the signing secret below.
          </p>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1.5">
              Signing Secret (whsec_…)
              {keys?.itemDeliveryWebhookSecretSet && <span className="ml-2 normal-case font-normal text-slate-400">— currently set ({keys.itemDeliveryWebhookSecretMasked})</span>}
            </label>
            <div className="relative">
              <input
                value={itemDeliveryWebhookSecret}
                onChange={e => setItemDeliveryWebhookSecret(e.target.value)}
                placeholder={keys?.itemDeliveryWebhookSecretSet ? "Enter new secret to replace" : "whsec_..."}
                type={showItemDeliveryWebhook ? "text" : "password"}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm outline-none focus:border-primary transition-colors font-mono"
              />
              <button onClick={() => setShowItemDeliveryWebhook(!showItemDeliveryWebhook)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showItemDeliveryWebhook ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded-xl text-sm transition-colors"
        >
          {saving ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={14} />}
          Save Stripe Settings
        </button>
      </div>
    </section>
  );
}

interface StoreStatus {
  id: number;
  name: string;
  stripeAccountId: string | null;
  stripeOnboardingComplete: boolean;
  ownerEmail: string | null;
  ownerName: string | null;
}

function ConnectedStoresSection() {
  const { toast } = useToast();
  const [stores, setStores] = useState<StoreStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState<Set<number>>(new Set());
  const [sendingAll, setSendingAll] = useState(false);

  const loadStores = () => {
    fetch("/api/admin/stripe/store-status", { headers: authHeaders() })
      .then(r => r.json())
      .then((d: StoreStatus[]) => setStores(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStores(); }, []);

  const sendEmail = async (storeIds: number[]) => {
    const res = await fetch("/api/admin/stripe/send-onboarding-email", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ storeIds }),
    });
    return res.json() as Promise<{ results: { storeId: number; sent: boolean; error?: string }[] }>;
  };

  const handleSendOne = async (storeId: number) => {
    setSending(prev => new Set(prev).add(storeId));
    try {
      const { results } = await sendEmail([storeId]);
      const r = results[0];
      if (r?.sent) toast({ title: "Setup email sent!" });
      else toast({ title: `Failed: ${r?.error ?? "Unknown error"}`, variant: "destructive" });
    } catch {
      toast({ title: "Failed to send email", variant: "destructive" });
    } finally {
      setSending(prev => { const s = new Set(prev); s.delete(storeId); return s; });
    }
  };

  const handleSendAll = async () => {
    const incomplete = stores.filter(s => !s.stripeOnboardingComplete && s.ownerEmail);
    if (incomplete.length === 0) { toast({ title: "No incomplete stores with email addresses" }); return; }
    setSendingAll(true);
    try {
      const { results } = await sendEmail(incomplete.map(s => s.id));
      const sent = results.filter(r => r.sent).length;
      const failed = results.filter(r => !r.sent).length;
      toast({ title: `Sent ${sent} email${sent !== 1 ? "s" : ""}${failed > 0 ? `, ${failed} failed` : ""}` });
    } catch {
      toast({ title: "Failed to send emails", variant: "destructive" });
    } finally {
      setSendingAll(false);
    }
  };

  const incomplete = stores.filter(s => !s.stripeOnboardingComplete);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Connected Stores</h2>
        <p className="text-sm text-slate-500 mt-1">Stripe onboarding status for all registered stores.</p>
      </div>

      {incomplete.length > 0 && (
        <button
          onClick={handleSendAll}
          disabled={sendingAll}
          className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-4 py-3 rounded-xl text-sm transition-colors"
        >
          {sendingAll && <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          Send Setup Email to All Incomplete ({incomplete.length})
        </button>
      )}

      {loading ? (
        <div className="text-sm text-slate-400">Loading stores…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[480px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left font-semibold text-slate-600 px-4 py-3">Store</th>
                <th className="text-left font-semibold text-slate-600 px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {stores.map(store => (
                <tr key={store.id} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{store.name}</p>
                    <p className="text-xs text-slate-400">{store.ownerEmail ?? "No email"}</p>
                  </td>
                  <td className="px-4 py-3">
                    {store.stripeOnboardingComplete
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><CheckCircle size={13} className="text-emerald-500" />Complete</span>
                      : store.stripeAccountId
                        // Started onboarding but Stripe has not enabled charges — the
                        // shop believes it is connected while card payments are off.
                        ? <span className="inline-flex items-center gap-1 text-red-700 font-medium" title="Stripe account connected but charges are not enabled — this store cannot take card payments"><XCircle size={13} className="text-red-500" />Connected — not enabled</span>
                        : <span className="inline-flex items-center gap-1 text-amber-700 font-medium"><XCircle size={13} className="text-amber-500" />Incomplete</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!store.stripeOnboardingComplete && (
                      <button
                        onClick={() => handleSendOne(store.id)}
                        disabled={sending.has(store.id)}
                        className="text-xs font-semibold text-primary hover:underline disabled:opacity-50 whitespace-nowrap"
                      >
                        {sending.has(store.id) ? "Sending…" : "Send Email"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminPricing() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [leadPrices, setLeadPrices] = useState<LeadPrice[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/pricing", { headers: authHeaders() });
      const data = await res.json() as { settings: Setting[]; leadPrices: LeadPrice[]; jobCategories: string[] };
      setSettings(data.settings ?? []);
      setLeadPrices(data.leadPrices ?? []);
      setCategories(data.jobCategories ?? []);
    } catch {
      toast({ title: "Failed to load pricing", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSetting = async (key: string, value: number, label: string) => {
    await fetch("/api/admin/pricing/settings", {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ key, value, label }),
    });
    setSettings(prev => {
      const existing = prev.find(s => s.key === key);
      if (existing) return prev.map(s => s.key === key ? { ...s, value } : s);
      return [...prev, { key, value, label }];
    });
    toast({ title: "Price saved" });
  };

  const saveLeadPrice = async (category: string, budgetBand: string, price: number) => {
    await fetch("/api/admin/pricing/leads", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ category, budgetBand, price }),
    });
    setLeadPrices(prev => {
      const exists = prev.find(p => p.category === category && p.budgetBand === budgetBand);
      if (exists) return prev.map(p => p.category === category && p.budgetBand === budgetBand ? { ...p, price } : p);
      return [...prev, { id: Date.now(), category, budgetBand, price }];
    });
    toast({ title: "Lead price saved" });
  };

  const getLeadPrice = (category: string, band: string) =>
    leadPrices.find(p => p.category === category && p.budgetBand === band)?.price ?? 0;

  const getSetting = (key: string) => settings.find(s => s.key === key)?.value ?? 0;

  return (
    <AdminLayout current="/admin/pricing">
      <div className="space-y-10">
        <div>
          <h1 className="text-2xl font-bold text-primary mb-1">Pricing & Stripe</h1>
          <p className="text-sm text-slate-500">Configure fees and connect your Stripe account. Set any price to £0 to make it free.</p>
        </div>

        <StripeSection />

        <ConnectedStoresSection />

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <>
            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Employer Access Fee</h2>
              <p className="text-sm text-slate-500 mb-4">
                One-time fee employers pay to unlock contact details and CVs in the "Looking for Work" tab.
                Set to £0 to make employer access free for everyone.
              </p>
              <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-xs">
                <p className="font-semibold text-slate-800">Employer Access</p>
                <p className="text-xs text-slate-500 mb-3">Paid once — employer can then view all seeker profiles</p>
                <PriceInput
                  value={getSetting("employer_access_fee")}
                  onSave={(v) => saveSetting("employer_access_fee", v, "Employer Access Fee")}
                />
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Order Local — Store Subscription</h2>
              <p className="text-sm text-slate-500 mb-4">
                Weekly fee charged to shops listed on the Order Local feature. Paid via Stripe to your platform account.
                After admin approval, shops must subscribe before their store goes live. Set to £0 to make it free.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-xs">
                  <p className="font-semibold text-slate-800">Weekly Listing Fee</p>
                  <p className="text-xs text-slate-500 mb-3">Charged every week — store goes inactive if payment fails</p>
                  <PriceInput
                    value={getSetting("order_store_weekly_fee") || 1500}
                    onSave={(v) => saveSetting("order_store_weekly_fee", v, "Order Store Weekly Fee")}
                  />
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-xs">
                  <p className="font-semibold text-slate-800">Customer Order Fee</p>
                  <p className="text-xs text-slate-500 mb-3">Added to every customer order and routed to your Stripe account via Stripe Connect application fee</p>
                  <PriceInput
                    value={getSetting("platform_order_fee_pence") || 50}
                    onSave={(v) => saveSetting("platform_order_fee_pence", v, "Platform Order Fee")}
                  />
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Multidrop run fees</h2>
              <p className="text-sm text-slate-500 mb-4">
                For "request a driver → multiple drops". The driver keeps every drop fee the shop sets, plus a fuel bonus; the Hub charges a fee on top. Both grow with each drop after the first.
              </p>
              {(() => {
                const capSetting = settings.find(s => s.key === "external_run_hub_fee_cap_pence");
                const capValue = capSetting ? capSetting.value : 500; // absent = default 500
                const capEnabled = capValue > 0;                       // 0 = no cap
                return (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <p className="font-semibold text-slate-800">Driver fuel bonus — per extra drop</p>
                      <p className="text-xs text-slate-500 mb-3">Added to the shop's own fuel supplement for each drop after the first.</p>
                      <PriceInput value={getSetting("external_run_fuel_per_stop_pence") || 30} onSave={(v) => saveSetting("external_run_fuel_per_stop_pence", v, "Run fuel per extra drop")} />
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <p className="font-semibold text-slate-800">Hub fee — base (single drop)</p>
                      <p className="text-xs text-slate-500 mb-3">The Hub's fee for a one-drop run.</p>
                      <PriceInput value={getSetting("external_run_hub_fee_base_pence") || 100} onSave={(v) => saveSetting("external_run_hub_fee_base_pence", v, "Run hub fee base")} />
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <p className="font-semibold text-slate-800">Hub fee — per extra drop</p>
                      <p className="text-xs text-slate-500 mb-3">Added to the Hub fee for each drop after the first.</p>
                      <PriceInput value={getSetting("external_run_hub_fee_per_extra_pence") || 50} onSave={(v) => saveSetting("external_run_hub_fee_per_extra_pence", v, "Run hub fee per extra drop")} />
                    </div>
                    <div className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <p className="font-semibold text-slate-800">Hub fee — cap</p>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={capEnabled}
                            onChange={(e) => saveSetting("external_run_hub_fee_cap_pence", e.target.checked ? (capValue > 0 ? capValue : 500) : 0, "Run hub fee cap")}
                          />
                          Cap the hub fee
                        </label>
                      </div>
                      <p className="text-xs text-slate-500 mb-3">
                        {capEnabled ? "The Hub fee never exceeds this, no matter how many drops." : "No cap — the Hub fee keeps growing with every extra drop."}
                      </p>
                      {capEnabled && (
                        <PriceInput value={capValue} onSave={(v) => saveSetting("external_run_hub_fee_cap_pence", v, "Run hub fee cap")} />
                      )}
                    </div>
                  </div>
                );
              })()}
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Listing Fees</h2>
              <p className="text-sm text-slate-500 mb-4">Charged when someone posts a deal or event. Set to £0 for free listings.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { key: "deal_listing_fee", label: "Deal Listing Fee", desc: "Charged when a business submits a deal" },
                  { key: "event_listing_fee", label: "Event Listing Fee", desc: "Charged when a business submits an event" },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="bg-white border border-slate-200 rounded-xl p-5">
                    <p className="font-semibold text-slate-800">{label}</p>
                    <p className="text-xs text-slate-500 mb-3">{desc}</p>
                    <PriceInput
                      value={getSetting(key)}
                      onSave={(v) => saveSetting(key, v, label)}
                    />
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Sponsored / Featured Upgrade</h2>
              <p className="text-sm text-slate-500 mb-4">
                Businesses pay to pin their deal or event at the top of listings for a set duration.
                Set to £0 to offer free upgrades. Prices are per listing, not per week.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="text-left font-semibold text-slate-600 py-2 pr-6 min-w-[120px]">Content Type</th>
                      <th className="text-center font-semibold text-slate-600 py-2 px-4 min-w-[110px]">1 Week</th>
                      <th className="text-center font-semibold text-slate-600 py-2 px-4 min-w-[110px]">2 Weeks</th>
                      <th className="text-center font-semibold text-slate-600 py-2 px-4 min-w-[110px]">4 Weeks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { type: "deal", label: "Deals", icon: "🏷️" },
                      { type: "event", label: "Events", icon: "📅" },
                      { type: "free_item", label: "Marketplace Items", icon: "🛍️" },
                    ].map(({ type, label, icon }, i) => (
                      <tr key={type} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                        <td className="py-3 pr-6">
                          <div className="flex items-center gap-2">
                            <span className="text-base">{icon}</span>
                            <span className="font-semibold text-slate-700">{label}</span>
                          </div>
                        </td>
                        {([1, 2, 4] as const).map((w) => {
                          const key = `sponsored_${type}_${w}w`;
                          const label2 = `Sponsored ${label} – ${w} week${w > 1 ? "s" : ""}`;
                          return (
                            <td key={w} className="py-3 px-4 text-center">
                              <PriceInput
                                value={getSetting(key)}
                                onSave={(v) => saveSetting(key, v, label2)}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Sponsored items appear pinned at the top of their listing page for the chosen duration, then revert to standard ordering automatically.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-2">Lead Reveal Prices</h2>
              <p className="text-sm text-slate-500 mb-4">
                Set the price a tradesperson pays to reveal contact details for a job lead, based on trade category and budget.
              </p>

              {categories.length === 0 ? (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
                  No job trade categories set up yet. Add trades in the Categories &amp; Trades page first.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr>
                        <th className="text-left font-semibold text-slate-600 py-2 pr-4 min-w-[140px]">Trade Category</th>
                        {BUDGET_BANDS.map(band => (
                          <th key={band} className="text-center font-semibold text-slate-600 py-2 px-2 min-w-[110px]">
                            {band}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((cat, i) => (
                        <tr key={cat} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                          <td className="py-2 pr-4 font-medium text-slate-700 rounded-l">{cat}</td>
                          {BUDGET_BANDS.map(band => (
                            <td key={band} className="py-2 px-2 text-center">
                              <PriceInput
                                value={getLeadPrice(cat, band)}
                                onSave={(v) => saveLeadPrice(cat, band, v)}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Item Delivery (Hub Courier)</h2>
              <p className="text-sm text-slate-500 mb-4">
                Pricing for the Hub Dash courier service. Drivers collect and deliver parcels, keys, Screwfix orders, etc.
              </p>
              <ItemDeliverySettings />
            </section>

            <section>
              <h2 className="text-lg font-semibold text-slate-800 mb-1">Driver Dispatch</h2>
              <p className="text-sm text-slate-500 mb-4">
                Configure how long drivers have to accept a job offer before it expires and moves to the next wave.
                Applies to both food deliveries and Hub Dash courier jobs.
              </p>
              <DriverDispatchSettings />
            </section>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function ItemDeliverySettings() {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    pricePerMilePence: 150,
    driverPercent: 75,
    minFeePence: 500,
    maxDistanceMiles: 20,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/api/admin/item-delivery-settings", { headers: authHeaders() })
      .then(r => r.json())
      .then((d: typeof settings) => { setSettings(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  async function save() {
    setSaving(true);
    try {
      await fetch("/api/admin/item-delivery-settings", {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(settings),
      });
      toast({ title: "Item delivery settings saved" });
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="h-24 bg-slate-100 rounded-xl animate-pulse" />;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5 max-w-lg">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700">Price per mile (pence)</label>
          <p className="text-xs text-slate-400">e.g. 150 = £1.50/mile · billed per 0.1 mile</p>
          <input
            type="number" min="1" step="1"
            value={settings.pricePerMilePence}
            onChange={e => setSettings(s => ({ ...s, pricePerMilePence: parseInt(e.target.value) || 0 }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700">Driver % of fee</label>
          <p className="text-xs text-slate-400">How much the driver earns</p>
          <input
            type="number" min="1" max="100" step="1"
            value={settings.driverPercent}
            onChange={e => setSettings(s => ({ ...s, driverPercent: parseInt(e.target.value) || 0 }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700">Minimum fee (pence)</label>
          <p className="text-xs text-slate-400">e.g. 500 = £5.00 minimum</p>
          <input
            type="number" min="1" step="1"
            value={settings.minFeePence}
            onChange={e => setSettings(s => ({ ...s, minFeePence: parseInt(e.target.value) || 0 }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700">Max distance (miles)</label>
          <p className="text-xs text-slate-400">Refuse quotes beyond this</p>
          <input
            type="number" min="1" max="100" step="1"
            value={settings.maxDistanceMiles}
            onChange={e => setSettings(s => ({ ...s, maxDistanceMiles: parseFloat(e.target.value) || 0 }))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>
      <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
        <strong>Example:</strong> a 3.0-mile delivery at {settings.pricePerMilePence}p/mile (billed per 0.1 mi) = £{((3.0 * settings.pricePerMilePence) / 100).toFixed(2)}.
        {" "}Driver earns {settings.driverPercent}% = £{(Math.max(3 * settings.pricePerMilePence, settings.minFeePence) * settings.driverPercent / 10000).toFixed(2)}.
        Minimum charge: £{(settings.minFeePence / 100).toFixed(2)}.
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Item Delivery Settings"}
      </button>
    </div>
  );
}

function DriverDispatchSettings() {
  const { toast } = useToast();
  const [offerSeconds, setOfferSeconds] = useState(120);
  const [refundMinutes, setRefundMinutes] = useState(60);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/api/admin/pricing", { headers: authHeaders() })
      .then(r => r.json())
      .then((d: { settings: Array<{ key: string; value: number }> }) => {
        const offerVal = d.settings?.find(s => s.key === "driver_offer_timeout_seconds")?.value;
        if (offerVal != null) setOfferSeconds(offerVal);
        const refundVal = d.settings?.find(s => s.key === "dispatch_auto_refund_delay_minutes")?.value;
        if (refundVal != null) setRefundMinutes(refundVal);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  async function saveSetting(key: string, value: number, label: string) {
    await fetch("/api/admin/pricing/settings", {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ key, value, label }),
    });
  }

  async function save() {
    setSaving(true);
    try {
      await Promise.all([
        saveSetting("driver_offer_timeout_seconds", offerSeconds, "Driver Offer Timeout"),
        saveSetting("dispatch_auto_refund_delay_minutes", refundMinutes, "Auto-refund delay (minutes)"),
      ]);
      toast({ title: "Driver dispatch settings saved" });
    } catch {
      toast({ title: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="h-24 bg-slate-100 rounded-xl animate-pulse" />;

  const offerMins = Math.floor(offerSeconds / 60);
  const offerSecs = offerSeconds % 60;
  const offerLabel = offerMins > 0 ? `${offerMins}m ${offerSecs > 0 ? `${offerSecs}s` : ""}`.trim() : `${offerSecs}s`;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-6 max-w-lg">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700">Job offer timeout (seconds)</label>
          <p className="text-xs text-slate-400">
            How long a driver has to accept before the offer expires and moves to the next wave (30–600s).
          </p>
          <input
            type="number" min="30" max="600" step="5"
            value={offerSeconds}
            onChange={e => setOfferSeconds(Math.max(30, Math.min(600, parseInt(e.target.value) || 120)))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <p className="text-xs text-slate-500 mt-1">Currently: {offerLabel}</p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-700">Auto-refund delay (minutes)</label>
          <p className="text-xs text-slate-400">
            How long the system keeps searching before auto-cancelling and refunding the customer (15–480 min). Admin can intervene any time before this.
          </p>
          <input
            type="number" min="15" max="480" step="5"
            value={refundMinutes}
            onChange={e => setRefundMinutes(Math.max(15, Math.min(480, parseInt(e.target.value) || 60)))}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <p className="text-xs text-slate-500 mt-1">Currently: {refundMinutes}m ({(refundMinutes / 60).toFixed(1)}hr)</p>
        </div>
      </div>

      <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        <strong>Auto-refund:</strong> If no driver accepts within the delay window, the order is cancelled and the customer is automatically refunded. Hub Dash escalates driver pay by 5% every 5 min before this kicks in.
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Dispatch Settings"}
      </button>
    </div>
  );
}
